import type { SetPause } from '@heraion/shared';

// Carry-forward resolution for the coach's per-set prescription cue
// (🎯 SetPrescription.notes), Antenore 2026-06-19 ("le note vengono mostrate
// solo nel primo set").
//
// WHY: in real programs the cue is overwhelmingly authored on the FIRST set
// only — it's an exercise-level coaching note dropped onto set 1. The card-logger
// used to render the cue on every SetRow; the timeline FocusPanel binds ONE set
// at a time, so a set-0-only cue showed only while set 0 was active.
//
// SOURCE (note-model consolidation, 2026-07-03): the cue is read ONLY from the
// plan, re-resolved per-slug by usePlanCues at render — never from the draft
// set. So it's correct across fresh-start / edit / repeat AND across an
// in-workout swap (the cue follows the exercise, not the slot). Index-aligned
// with the planned sets.
//
// MODEL (sticky / carry-forward): a cue applies from its set onward until a
// later set overrides it. So a set-0 cue shows on every set, while genuinely
// per-set cues (e.g. "Light" with a distinct note on each set) still differ.

// Walk back from the bound index to the nearest non-blank note (carry-forward).
function carryBack(
  notes: ReadonlyArray<string | undefined>,
  activeSetIdx: number,
): string | null {
  const start = Math.min(activeSetIdx, notes.length - 1);
  for (let i = start; i >= 0; i--) {
    const note = notes[i]?.trim();
    if (note) return note;
  }
  return null;
}

// Resolves the EFFECTIVE cue for the bound set = the plan cue on its set, else
// the nearest preceding planned set's cue (carry-forward). `cues` is the plan's
// per-set cues re-resolved from the source program/plan (usePlanCues).
export function effectivePrescriptionNote(
  activeSetIdx: number | null,
  cues?: ReadonlyArray<string | undefined>,
): string | null {
  if (activeSetIdx === null || !cues) return null;
  return carryBack(cues, activeSetIdx);
}

// HI-8: the pause inside the rep of the bound set, from the same plan source.
// No carry-forward: a pause is authored per set, and a set without one has none.
export function prescribedPause(
  activeSetIdx: number | null,
  pauses?: ReadonlyArray<SetPause | undefined>,
): SetPause | null {
  if (activeSetIdx === null || !pauses) return null;
  return pauses[activeSetIdx] ?? null;
}
